import React, { useState, useEffect, useCallback } from 'react';
import { PhotoIcon, PlusIcon, TrashIcon, EyeIcon } from '@heroicons/react/24/outline';
import { message } from 'antd';
import apiService from '../services/api';

interface TeamActivity {
  _id: string;
  title: string;
  description?: string;
  imageUrl: string;
  link?: string;
  order: number;
  isActive: boolean;
  isAdminCreated: boolean;
  createdAt: string;
  updatedAt: string;
}

interface TeamAlbumProps {
  onImageSelect?: (imageUrl: string) => void;
  isSelectionMode?: boolean;
}

const TeamAlbum: React.FC<TeamAlbumProps> = ({ onImageSelect, isSelectionMode = false }) => {
  const [activities, setActivities] = useState<TeamActivity[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    title: '',
    description: '',
    link: ''
  });

  const fetchActivities = useCallback(async () => {
    setLoading(true);
    try {
      // 根据模式获取不同的数据
      const endpoint = isSelectionMode ? '/team-activities/admin' : '/team-activities';
      const response = await apiService.get<TeamActivity[]>(endpoint);
      if (response.success) {
        let activitiesData = response.data || [];
        
        // 在个人中心模式下，只显示已启用的活动
        if (!isSelectionMode) {
          activitiesData = activitiesData.filter(activity => activity.isActive);
        }
        
        setActivities(activitiesData);
      }
    } catch (error) {
      console.error('获取团队活动失败:', error);
      message.error('获取团队活动失败');
    } finally {
      setLoading(false);
    }
  }, [isSelectionMode]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await apiService.upload<{url: string}>('/upload/image', formData);
      if (response.success && response.data?.url) {
        // 创建新的团队活动
        const newActivity = {
          title: uploadForm.title || '团队活动',
          description: uploadForm.description || '',
          imageUrl: response.data.url,
          link: uploadForm.link || '',
          order: activities.length,
          isActive: true
        };

        const createResponse = await apiService.post('/team-activities/user', newActivity);
        if (createResponse.success) {
          message.success('图片上传成功，等待管理员审核');
          setShowUploadModal(false);
          setUploadForm({ title: '', description: '', link: '' });
          fetchActivities();
        }
      }
    } catch (error) {
      console.error('上传失败:', error);
      message.error('图片上传失败');
    } finally {
      setUploading(false);
    }
  };

  const handleImageSelect = (imageUrl: string) => {
    if (isSelectionMode && onImageSelect) {
      onImageSelect(imageUrl);
    } else {
      setSelectedImage(imageUrl);
    }
  };

  const handleDeleteActivity = async (id: string) => {
    try {
      const response = await apiService.delete(`/team-activities/${id}`);
      if (response.success) {
        message.success('删除成功');
        fetchActivities();
      }
    } catch (error) {
      console.error('删除失败:', error);
      message.error('删除失败');
    }
  };

  const closeImageModal = () => {
    setSelectedImage(null);
  };

  return (
    <div className="space-y-6">
      {/* 标题和操作按钮 */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            {isSelectionMode ? '选择图片' : '团队活动相册'}
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            {isSelectionMode ? '从团队活动相册中选择图片' : '管理团队活动照片'}
          </p>
        </div>
        {!isSelectionMode && (
          <button
            onClick={() => setShowUploadModal(true)}
            className="btn-primary flex items-center space-x-2"
          >
            <PlusIcon className="h-4 w-4" />
            <span>上传图片</span>
          </button>
        )}
      </div>

      {/* 图片网格 */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
          <p className="text-gray-600 mt-2">加载中...</p>
        </div>
      ) : activities.length === 0 ? (
        <div className="text-center py-12">
          <PhotoIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">暂无团队活动图片</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {activities.map((activity) => (
            <div
              key={activity._id}
              className="relative group bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
            >
              <div className="aspect-w-16 aspect-h-9">
                <img
                  src={activity.imageUrl}
                  alt={activity.title}
                  className="w-full h-48 object-cover cursor-pointer"
                  onClick={() => handleImageSelect(activity.imageUrl)}
                />
              </div>
              
              {/* 悬停操作按钮 */}
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center">
                <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex space-x-2">
                  <button
                    onClick={() => handleImageSelect(activity.imageUrl)}
                    className="p-2 bg-white rounded-full shadow-sm hover:shadow-md transition-shadow"
                    title="查看大图"
                  >
                    <EyeIcon className="h-4 w-4 text-gray-600" />
                  </button>
                  {!isSelectionMode && !(activity.isAdminCreated ?? false) && (
                    <button
                      onClick={() => handleDeleteActivity(activity._id)}
                      className="p-2 bg-red-500 text-white rounded-full shadow-sm hover:shadow-md transition-shadow"
                      title="删除"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* 图片信息 */}
              <div className="p-3">
                <div className="flex justify-between items-start mb-1">
                  <h4 className="text-sm font-medium text-gray-900 truncate flex-1">
                    {activity.title}
                  </h4>
                  {!isSelectionMode && (
                    <span className={`px-1 py-0.5 rounded text-xs ml-2 ${
                      activity.isActive 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {activity.isActive ? '已启用' : '待审核'}
                    </span>
                  )}
                </div>
                {activity.description && (
                  <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                    {activity.description}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 上传模态框 */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">上传团队活动图片</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  标题
                </label>
                <input
                  type="text"
                  value={uploadForm.title}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, title: e.target.value }))}
                  className="input-field"
                  placeholder="请输入活动标题"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  描述
                </label>
                <textarea
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, description: e.target.value }))}
                  className="input-field"
                  rows={3}
                  placeholder="请输入活动描述"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  链接（可选）
                </label>
                <input
                  type="url"
                  value={uploadForm.link}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, link: e.target.value }))}
                  className="input-field"
                  placeholder="请输入相关链接"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  选择图片
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="input-field"
                  disabled={uploading}
                />
                <div className="text-xs text-blue-600 mt-1">
                  💡 建议尺寸：1920×1080 或 16:9 比例，以获得最佳显示效果
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowUploadModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
                disabled={uploading}
              >
                取消
              </button>
              <button
                onClick={() => setShowUploadModal(false)}
                className="btn-primary"
                disabled={uploading}
              >
                {uploading ? '上传中...' : '完成'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 图片预览模态框 */}
      {selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="relative max-w-4xl max-h-full mx-4">
            <img
              src={selectedImage}
              alt="预览"
              className="max-w-full max-h-full object-contain"
            />
            <button
              onClick={closeImageModal}
              className="absolute top-4 right-4 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-75"
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamAlbum;
